import React, { useMemo, useState, useEffect } from "react";
import { Dropdown, Icon, Badge } from "rsuite";
import { ACCOUNT_USER_MODIFY_BY_SELF, ACCOUNT_USER_MODIFY_BY_SELF_IN_MANAGER_ACCOUNT } from "const/apiMethods";
import { checkPermissionsFor } from "store/storeHelpers";
import styled, { css } from "styled-components";
import * as S from "./styled.js";
import { APP_TYPE_ADMIN, APP_TYPE_CLIENT } from "const";
import { Link, useHistory } from "react-router-dom";
import { useIntl } from "react-intl";
import m from "definedMessages";
import { useWindowWidth } from "hooks";
import { withTranslation } from "hoc/WithTranslation.js";


const UserProfileMenu = ({
    appType,
    savedUser,
    absentTeamsContact,
    onChangeShowEditProfileModal,
    onChangeShowEditProfileContactsModal,

    normalizedPerms,
    permsReady,
    permSig,
    hasPerm,

    formatMessage
}) => {

    const windowWidth = useWindowWidth();
    const styled = getComputedStyle(document.documentElement);

    const history = useHistory();

    const [isEditable, setIsEditable] = useState(false);

    useEffect(() => {
        if (!permsReady) return;
        const next =
            hasPerm(ACCOUNT_USER_MODIFY_BY_SELF) ||
            (hasPerm(ACCOUNT_USER_MODIFY_BY_SELF_IN_MANAGER_ACCOUNT) && appType === APP_TYPE_ADMIN);

        setIsEditable(next);
    }, [permsReady, hasPerm, appType]);

    const badgeText = useMemo(() => savedUser
        ? appType !== APP_TYPE_CLIENT
            ? savedUser?.session?.account_user?.role_list[0]
            : savedUser.account_name
        : null,
        [savedUser, appType, isEditable]
    );

    useEffect(() => {
        if (absentTeamsContact) {
            onChangeShowEditProfileContactsModal(true)
        }
    }, [absentTeamsContact]);

    return (
        <>
            {isEditable
                ? <UserDropdown
                    menuStyle={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
                    title={windowWidth >= 480
                        ? <UserInfo>
                            <div className="user-account">
                                <AccountBadge badgeColor={styled.getPropertyValue("--color-status-badge")}>
                                    {badgeText}
                                </AccountBadge>
                            </div>
                            <UserIdentity>
                                <p className="user-name" title={savedUser?.name || ""}>
                                    {savedUser?.name}
                                </p>
                                {appType === APP_TYPE_CLIENT && savedUser?.account_nickname &&
                                    <p className="user-nickname" title={savedUser.account_nickname}>
                                        {savedUser.account_nickname}
                                    </p>
                                }
                            </UserIdentity>
                        </UserInfo>
                        : <StyledIconWrapper>
                            <Icon icon="gear-circle" size="lg" />
                        </StyledIconWrapper>
                    }
                >

                    <Dropdown.Item
                        eventKey="edit-profile"
                        onSelect={onChangeShowEditProfileModal}
                    >
                        <Icon icon="edit2" /> {formatMessage(m.editProfile)}
                    </Dropdown.Item>

                    {appType === APP_TYPE_CLIENT && <Dropdown.Item
                        eventKey="edit-profile-contacts"
                        onSelect={onChangeShowEditProfileContactsModal}
                    >
                        <Icon icon="avatar" /> {formatMessage(m.accountContacts)}
                    </Dropdown.Item>}


                    {appType === APP_TYPE_CLIENT &&
                        <Dropdown.Item
                            eventKey="payment-details"
                            onSelect={() => {
                                history.push("/payment-details")
                            }}
                        >
                            <Icon icon="usd" /> {formatMessage(m.managePaymentDetails)}
                        </Dropdown.Item>
                    }
                </UserDropdown>
                : <User>
                    <UserIdentity>
                        <span className="user-name" title={savedUser?.name || ""}>
                            {savedUser?.name}
                        </span>
                        {savedUser?.nickname &&
                            <span className="user-nickname" title={savedUser.nickname}>
                                {savedUser.nickname} 123
                            </span>
                        }
                    </UserIdentity>
                </User>
            }
        </>
    );
};

export default withTranslation(UserProfileMenu);

const UserDropdown = styled(S.StyledDropdown)`
    user-select: none;
    
    &&& {
    
        .rs-dropdown-toggle {
            align-items: center;
            border-radius: 0;
            box-sizing: border-box;
            display: flex;
            padding: 15px 16px 10px;
        }
        
        ${props => props.theme.appType === APP_TYPE_CLIENT && css`
            .rs-dropdown-toggle {
                padding-left: 10px;
                padding-right: 10px;
            }
        `}

        .rs-dropdown-toggle-caret {
            margin: 0;
            position: static;
            transform: none;
        }
    }
`;

const AccountBadge = styled(Badge)`
    padding-left: 6px;
    padding-top: 1px;
    
    .rtl & {
        padding-left: 0;
        padding-right: 6px;
    }

    &&& {

        .rs-badge-content {
            height: 4px;
            left: 0; 
            top: 0;
            width: 4px;
            transform: none;

            ${props =>
        props.badgeColor &&
        css`
                    background-color: ${props.badgeColor}
                `
    }
        }
    }
    
    .rtl &&& {
        
        .rs-badge-content {
            left: auto;
            right: 0;
        }
    }
`;

const UserInfo = styled.div`
    .user-account {
        font-size: 8px;
        left: 2px;
        line-height: 1.2em;
        position: absolute;
        top: 2px;
    }
    
    ${props => props.theme.appType === APP_TYPE_CLIENT && css`
        .user-account {
            text-transform: uppercase;
        }
    `}

    .user-name,
    .user-nickname {
        margin: 0;
        max-width: 150px;
        min-width: 0;
        overflow: hidden;
        padding-right: 10px;
        text-align: left;
        text-overflow: ellipsis;
        white-space: nowrap;

        @media (min-width: 768px) {
            min-width: 130px;
        }
    }

    .user-nickname {
        font-size: 10px;
        line-height: 11px;
        opacity: 0.75;
        color: #d6ffda;
        font-weight: 600;
    }
    
    .rtl & {
        
        .user-account {
            left: auto;
            right: 2px;
        }
        
        .user-name,
        .user-nickname {
            padding-left: 10px;
            padding-right: 0;
            text-align: right;
        }
    }
`;

const UserIdentity = styled.div`
    min-width: 0;
    position: relative;

    .user-nickname {
        left: 0;
        position: absolute;
        top: 36px;
        width: 100%;
    }

    .rtl & .user-nickname {
        left: auto;
        right: 0;
    }
`;


const User = styled.div`
    display: inline-block;
    padding: 19px 16px;
    height: 56px;
    font-size: 13px;
    color: #fff;

    .user-name,
    .user-nickname {
        display: block;
        max-width: 150px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .user-nickname {
        font-size: 10px;
        line-height: 11px;
        opacity: 0.75;
    }
`;

const StyledIconWrapper = styled.div`
    padding: 0 10px;
`;
